/**
 * `lastLooked` — pure client-side tracking of "when this member last looked at
 * this team's map," for the B3.6 "what changed since you last looked" digest
 * (issue #765). No React, no network: a thin, defended wrapper over
 * `localStorage` so the digest hook + the panel + the tests all read/write the
 * same key, the same way.
 *
 * Why client-side: the digest's `since` is "the last time YOU opened this
 * map" — a per-member, per-device orientation cue, not a server fact. Keeping
 * it in `localStorage` keyed by `(team, member)` means it is the member's own,
 * on their terms (§A9: the digest is calm + on the member's terms), and never
 * leaks to or aggregates across other people.
 *
 * Brand firewall (§A9): this stamp is purely "when did I last visit." It is
 * never a tally, never compared between people, never sent anywhere it could
 * become a per-person metric. It exists only to scope the member's OWN digest.
 *
 * Defensive: every access is wrapped — a disabled / full / throwing
 * `localStorage` (private mode, quota) degrades to "no record" / a no-op,
 * never a crash. A missing stamp means "first visit" — the caller falls back
 * to a sensible default window.
 */

const KEY_PREFIX = "tarii.taskmap.lastLooked";

/** Per-team, per-member storage key. A null member id is its own bucket. */
export function lastLookedKey(teamId: string, membershipId: string | null): string {
  return `${KEY_PREFIX}.${teamId}.${membershipId ?? "anon"}`;
}

/**
 * Read the ISO instant this member last looked at this team's map, or null if
 * there is no record (first visit, or storage unavailable). Validates the
 * stored value parses as a date; a corrupt value reads as "no record."
 */
export function readLastLooked(teamId: string, membershipId: string | null): string | null {
  try {
    const raw = window.localStorage.getItem(lastLookedKey(teamId, membershipId));
    if (!raw) return null;
    return Number.isNaN(new Date(raw).getTime()) ? null : raw;
  } catch {
    return null;
  }
}

/**
 * Stamp "this member just looked at this team's map" with an ISO instant
 * (defaults to now). A no-op if storage is unavailable. Returns the value
 * written (or attempted), so a caller can also use it as "now."
 */
export function writeLastLooked(
  teamId: string,
  membershipId: string | null,
  at: Date = new Date(),
): string {
  const iso = at.toISOString();
  try {
    window.localStorage.setItem(lastLookedKey(teamId, membershipId), iso);
  } catch {
    // Storage unavailable (private mode / quota) — degrade silently; the
    // digest just recomputes against the default window next visit.
  }
  return iso;
}

/** Default look-back when there is no stored "last looked" (a first visit). */
export const DEFAULT_LOOKBACK_DAYS = 7;

/**
 * The `since` instant for the digest query: the stored "last looked," or — on
 * a first visit — a default look-back window from `now` (so a new member still
 * gets a calm, bounded orientation rather than the whole history).
 */
export function resolveSince(
  teamId: string,
  membershipId: string | null,
  now: Date = new Date(),
): string {
  const stored = readLastLooked(teamId, membershipId);
  if (stored) return stored;
  const fallback = new Date(now.getTime() - DEFAULT_LOOKBACK_DAYS * 24 * 60 * 60 * 1000);
  return fallback.toISOString();
}
